import { useState, useEffect } from 'react';
import { API_ENDPOINT, logActivity, useVerifyAdmin } from 'helper';
import dayjs from 'dayjs';
import { DeviceTypes } from '../constants';
import { verifySingleStack } from '../helper';
import { updateDevice } from '../service';
import { useProfileData } from 'components/HGProfile/helper';

export const useDeviceProfileFields = () => {
    const isAdmin = useVerifyAdmin();
    const [deviceProfileFields, setDeviceProfileFields] = useState(null);

    const {
        data: profileData,
        loading,
        refetch: refetchProfileData,
        deviceType,
        bmsConfigData,
        moduleConfigData,
    } = useProfileData();

    useEffect(() => {
        if (profileData && !deviceProfileFields) {
            const {
                ThingName,
                ThingType,
                SerialNumber,
                SiteInfo,
                WarrantyStart,
            } = profileData;
            const { Name: SiteName } = SiteInfo ?? {};
            const isGateway =
                deviceType === DeviceTypes.EMS ||
                deviceType === DeviceTypes.NEP;

            const fields = {
                serialNumber:
                    moduleConfigData?.PACK_SN ??
                    bmsConfigData?.MASTER_SN ??
                    SerialNumber ??
                    '',
                name:
                    isGateway || deviceType === DeviceTypes.BMS
                        ? ThingName
                        : (moduleConfigData?.PackName ??
                          bmsConfigData?.MasterName ??
                          ''),
                type: isGateway ? ThingType : deviceType,
            };

            if (isAdmin) {
                //Warranty only for admin
                fields.WarrantyStart = WarrantyStart ?? '';
                //Detach site option only for admin
                fields.siteAttachedTo = SiteName ?? '';
            }

            setDeviceProfileFields(fields);
        }
    }, [
        deviceProfileFields,
        deviceType,
        isAdmin,
        profileData,
        bmsConfigData,
        moduleConfigData,
    ]);

    return {
        deviceProfileFields,
        loading,
        refetchProfileData,
    };
};

export const updateDeviceProfile = async (payload, device, accessToken) => {
    try {
        const { isSingleStack } = verifySingleStack(device);
        const updatedDeviceProfile = {
            Thing_ID: device.Thing_ID,
            Customer_ID: device.Customer_ID,
        };

        // for Non-singlestack BMS and modules
        if (
            updatedDeviceProfile.Names &&
            ((payload?.type === DeviceTypes.BMS && !isSingleStack) ||
                payload?.type === DeviceTypes.MODULE)
        ) {
            updatedDeviceProfile.Names = JSON.stringify({
                ...device?.Names,
                [payload.serialNumber]: payload.name,
            });
        } else {
            updatedDeviceProfile.ThingName = payload.name;
        }

        // Deleting/ detaching site
        if (
            payload?.siteAttachedTo === 'marked for detachment' &&
            device?.Site_ID
        ) {
            updatedDeviceProfile.Site_ID = null;
            updatedDeviceProfile.Installed = false;
        }

        let updatedProfile;

        if (payload?.warranty !== device?.warranty) {
            const updateWarrantyResponse = await updateWarranty(
                payload,
                device,
                accessToken
            );

            updatedProfile = updateWarrantyResponse;
        }

        if (
            payload?.name !== device?.ThingName ||
            payload?.siteAttachedTo !== device?.SiteInfo?.Name
        ) {
            const updatedDevice = await updateDevice(
                accessToken,
                updatedDeviceProfile
            );

            if (!updatedDevice) {
                throw new Error('Failed to update device profile.');
            }

            updatedProfile = { ...updatedProfile, ...updatedDevice };
        }

        logActivity(
            'User',
            'Info',
            'Update device profile',
            `Device ${device.SerialNumber} profile updated successfully.`,
            {
                before: device,
                after: updatedProfile,
            }
        );

        return updatedProfile;
    } catch (error) {
        console.error('Error updating device profile:', error);
        logActivity(
            'User',
            'Error',
            'Update device profile',
            `Error updating device profile for ${device.SerialNumber}: ${error.errors[0].message}`,
            {
                before: device,
                after: error,
            },
            'error'
        );
        throw error;
    }
};

export const updateWarranty = async (payload, device = {}, accessToken) => {
    const url = new URL(`${API_ENDPOINT}/warranties`);
    try {
        const { serialNumber, type, userConfirmation = false } = payload;
        const newWarrantyStartDate = dayjs(payload?.warranty).format(
            'M/DD/YYYY'
        );
        const updatedWarrantyResponse = await fetch(url.toString(), {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                Authorization: accessToken,
            },
            body: JSON.stringify({
                thingId: device?.Thing_ID ?? `BG_${serialNumber}`,
                serialNumber,
                deviceType: type,
                warrantyStartDate: newWarrantyStartDate,
                userConfirmation,
            }),
        });

        if (!updatedWarrantyResponse.ok) {
            throw new Error(updatedWarrantyResponse.error);
        }

        if (updatedWarrantyResponse.ok) {
            logActivity(
                'User',
                'Info',
                'Update warranty',
                `User updated warranty for device ${payload.serialNumber} successfully.`,
                {
                    before: device,
                    after: updatedWarrantyResponse.data,
                }
            );
        }

        return await updatedWarrantyResponse.json();
    } catch (error) {
        logActivity(
            'User',
            'Error',
            'Update warranty',
            `Error updating warranty for device ${payload.serialNumber}: ${error}`,
            {
                before: device.warranty,
                after: error,
            }
        );
        throw error;
    }
};
